import { InboxOutlined } from '@ant-design/icons';
import { Upload, Progress, message } from 'antd';
import type { UploadProps } from 'antd/es/upload';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommonModal from '../common';

const { Dragger } = Upload;

interface ImportStudentToCourseModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmSuccess?: () => void;
  onDownloadTemplate?: () => void;
  uploadApi: (formData: FormData) => Promise<void>;
  title?: string;
  fileTypeLabel?: string;
  acceptedFileType?: string;
  maxSizeMB?: number;
}

const ImportStudentToCourseModal: React.FC<ImportStudentToCourseModalProps> = ({
  open,
  onClose,
  onConfirmSuccess,
  onDownloadTemplate,
  uploadApi,
  title = 'Import dữ liệu từ file',
  fileTypeLabel = 'Excel (.xlsx)',
  acceptedFileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  maxSizeMB = 10,
}) => {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [isDisableSaveButton, setIsDisableSaveButton] = useState(true);
  const [uploadKey, setUploadKey] = useState(Date.now());

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    message.warning('Đang import File, vui lòng đợi...');
    setImporting(true);
    setUploadProgress(0);
    setIsDisableSaveButton(true);

    let fakeProgress = 0;
    const interval = setInterval(() => {
      fakeProgress += 5;
      if (fakeProgress >= 70) {
        clearInterval(interval);
        return;
      }
      setUploadProgress(fakeProgress);
    }, 200);

    try {
      await uploadApi(formData);
      clearInterval(interval);
      setUploadProgress(100);
      // setStudentListImport(res.data.data)
      message.success('Import thành công!');
      setIsDisableSaveButton(false);
    } catch (err) {
      clearInterval(interval);
      setUploadProgress(0);
      setIsDisableSaveButton(true);
      message.error('Đã có lỗi khi import file!');
    } finally {
      setImporting(false);
    }
  };

  const propsFile: UploadProps = {
    name: 'file',
    maxCount: 1,
    beforeUpload: (file) => {
      const isAcceptType = file.type === acceptedFileType;
      const isValidSize = file.size / 1024 / 1024 <= maxSizeMB;
      if (!isAcceptType) {
        message.error(`${file.name} ${t("không đúng định dạng", "không đúng định dạng")} ${fileTypeLabel}`);
        return false;
      }
      if (!isValidSize) {
        message.error(`${file.name} ${t("vượt quá", "vượt quá")} ${maxSizeMB}MB`);
        return false;
      }
      setFileList([{ name: file.name }]);
      handleUpload(file);
      return false;
    },
    showUploadList: false,
  };

  useEffect(() => {
    setFileList([]);
    setUploadProgress(0);
    setUploadKey(Date.now());
  }, [open]);
  return (
    <CommonModal
      open={open}
      title={title}
      desc={
        <div className="border-b pb-3">
          <Dragger {...propsFile}
            key={uploadKey}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t("Nhấn hoặc kéo thả tệp", "Nhấn hoặc kéo thả tệp")} {fileTypeLabel} {t("vào đây", "vào đây")}</p>
            <p className="ant-upload-hint">
              {t("Hỗ trợ định dạng:", "Hỗ trợ định dạng:")} {fileTypeLabel} - {t("Tối đa", "Tối đa")} {maxSizeMB}MB
            </p>
          </Dragger>

          {uploadProgress > 0 && <Progress percent={uploadProgress} />}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="loader-upload-file" data-text={t("Hệ thống đang xử lý, xin vui lòng chờ.....", "Hệ thống đang xử lý, xin vui lòng chờ.....")} />
          )}
          {uploadProgress === 100 && <div>{fileList[0]?.name}</div>}

          <div className="pt-3">
            <span className="font-bold">
              {t("Hướng dẫn các bước thêm sinh viên từ file Excel:", "Hướng dẫn các bước thêm sinh viên từ file Excel:")}
            </span>
            <ol className="list-decimal px-4">
              <li className="list-decimal">
                {t("Tải mẫu file Excel chuẩn mà bạn cần sử dụng", "Tải mẫu file Excel chuẩn mà bạn cần sử dụng")}{" "}
                <span
                  className="text-[#1677FF] cursor-pointer"
                  onClick={onDownloadTemplate}
                >
                  {t("tại đây", "tại đây")}
                </span>
              </li>
              <li className="list-decimal">
                {t("Nhấn hoặc thả tệp cần tải lên vào khu vực tải tệp", "Nhấn hoặc thả tệp cần tải lên vào khu vực tải tệp")}
              </li>
              <li className="list-decimal">
                {t("Chọn nút “Lưu“ để tải file lên hệ thống", "Chọn nút “Lưu“ để tải file lên hệ thống")}
              </li>
            </ol>
          </div>
        </div>
      }
      closeIcon
      okText={t("Lưu", "Lưu")}
      cancelText={t("Huỷ", "Huỷ")}
      icon=""
      loading={importing}
      disableButton={isDisableSaveButton}
      confirm={() => {
        if (onConfirmSuccess) {
          onConfirmSuccess();
        }
      }} closeModal={onClose}
      className="!w-full md:!w-[520px]"
      maskClosable={false}
    />
  );
};

export default ImportStudentToCourseModal;
